/**
 * Deleting the account (D-89, salon-mockup.html 17a–17d). Apple 5.1.1(v) and
 * Google Play require it inside the app. Erased at once, future bookings go
 * silently, the password is the check, no email is sent (owner, 2026-10-09).
 *
 * 17a and 17b are one screen: 17b is 17a with the error line under the
 * password. 17c is the 14b menu, mid-screen on desktop. 17d stands outside
 * the signed-in guard, because by the time it shows she is signed out.
 */
import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useAuth } from '../auth';
import { ActionMenu } from '../components/ActionMenu';
import { AppBar, Shell } from '../components/Shell';
import { ApiError, api } from '../core/api';
import { errorText } from '../i18n';
import { AuthFrame } from './Auth';

const ITEMS = ['itemAccount', 'itemClients', 'itemBookings', 'itemSalon', 'itemChannels'] as const;

/** 17b: every failure leaves her here, signed in, with nothing deleted. */
function failureText(err: unknown, t: (key: string) => string): string {
  if (!(err instanceof ApiError)) return t('accountDelete.failed');
  if (err.code === 'WRONG_PASSWORD') return t('accountDelete.wrongPassword');
  if (err.code === 'NETWORK_ERROR' || err.code === 'OFFLINE_MUTATION_BLOCKED') return t('accountDelete.offline');
  if (err.code === 'TOO_MANY_ATTEMPTS') return errorText(err.code);
  return t('accountDelete.failed');
}

export function AccountDeleteScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountDeleted } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<{ text: string; wrongPassword: boolean } | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const button = useRef<HTMLButtonElement>(null);

  const ask = (e: FormEvent) => {
    e.preventDefault();
    setAsking(true);
  };

  const remove = async () => {
    setAsking(false);
    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount(password);
    } catch (err) {
      setBusy(false);
      setError({
        text: failureText(err, t),
        wrongPassword: err instanceof ApiError && err.code === 'WRONG_PASSWORD',
      });
      return;
    }
    // Leave the signed-in screens first, so the guard does not send her to /login.
    navigate('/account-deleted', { replace: true });
    await accountDeleted();
  };

  return (
    <Shell active="settings">
      <AppBar title={t('accountDelete.title')} back={() => navigate('/settings')} />
      <div className="content">
        <form className="pane" onSubmit={ask} noValidate>
          <p style={{ fontSize: 14.5, lineHeight: 1.45, margin: '2px 2px 14px' }}>{t('accountDelete.lead')}</p>
          <span className="cap">{t('accountDelete.what')}</span>
          <div className="seclist">
            {ITEMS.map((key) => (
              <div key={key} className="si" style={{ cursor: 'default' }}>
                <span className="grow sm" style={{ color: 'var(--ink)' }}>
                  {t(`accountDelete.${key}`)}
                </span>
              </div>
            ))}
          </div>
          <div className="warnbox">{t('accountDelete.warn')}</div>
          <div className="fld">
            <label>
              {t('accountDelete.password')}
              <input
                className={`inp${error?.wrongPassword ? ' bad' : ''}`}
                type="password"
                autoComplete="current-password"
                aria-invalid={error?.wrongPassword || undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && (
              <span className="field-error" role="alert">
                {error.text}
              </span>
            )}
          </div>
          <button ref={button} type="submit" className="btn dgr blk" disabled={busy}>
            {t('accountDelete.deleteAccount')}
          </button>
        </form>
      </div>
      {asking && button.current && (
        <ActionMenu
          anchor={button.current}
          centered
          title={t('accountDelete.confirmTitle')}
          note={t('accountDelete.confirmText')}
          action={t('accountDelete.confirm')}
          cancel={t('accountDelete.cancel')}
          onAction={() => void remove()}
          onClose={() => setAsking(false)}
        />
      )}
    </Shell>
  );
}

export function AccountDeletedScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <AuthFrame>
      <div className="login">
        <div className="loginbox" style={{ textAlign: 'center' }}>
          <span className="ava" aria-hidden="true" style={{ margin: '0 auto 16px', width: 64, height: 64 }}>
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12.5l5.5 5.5L20 7" />
            </svg>
          </span>
          <h2 style={{ fontSize: 22, letterSpacing: '-.5px', margin: '0 0 8px' }}>{t('accountDelete.doneTitle')}</h2>
          <p className="sm" style={{ margin: '0 0 22px', lineHeight: 1.45 }}>
            {t('accountDelete.doneText')}
          </p>
          <button type="button" className="btn pri blk" onClick={() => navigate('/register', { replace: true })}>
            {t('accountDelete.done')}
          </button>
        </div>
      </div>
    </AuthFrame>
  );
}
